import React, { useEffect, useRef, useState } from 'react';
import '../../styles/FluxoAnimado.css';

/**
 * FluxoAnimado.js — o ciclo do estacionamento, da chegada à saída do veículo
 * CI Sistemas de Informática Ltda. — AcessoiD (Onda 92)
 *
 * POR QUE UMA ANIMAÇÃO, E NÃO UM TEXTO
 *
 * O ciclo do estacionamento tem uma coisa que texto explica mal: as peças agem
 * ao mesmo tempo, em lugares diferentes. A câmera lê enquanto o servidor decide,
 * enquanto a impressora imprime, enquanto o app da guarita mostra o veículo
 * aparecendo na lista. Quem lê "o sistema registra a entrada e imprime o ticket"
 * não vê que são três coisas em três lugares.
 *
 * A dúvida que esta animação resolve, e que aparece toda semana no G2: **por que
 * a cancela não abriu se o cliente pagou?** A resposta é que o pagamento não abre
 * a cancela — quem abre é o reconhecimento da placa na saída, conferindo que
 * aquele ticket está pago. Ver o carro andar até a cancela e só então a leitura
 * acontecer ensina isso em cinco segundos.
 *
 * POR QUE SVG DESENHADO À MÃO, E NÃO VÍDEO EMBUTIDO
 *
 * Um MP4 de 30 s pesa alguns megabytes, não escala em tela de celular sem borrar
 * e não dá para pausar num passo específico. Este SVG pesa alguns kilobytes,
 * fica nítido em qualquer zoom e tem controle de passo — o operador para no
 * quadro que interessa e lê a legenda. O MP4 existe à parte, para o YouTube, que
 * é outro público e outro contexto.
 *
 * `prefers-reduced-motion` desliga o avanço automático: quem pediu menos
 * movimento ainda navega pelos passos, botão a botão.
 */

// ─── Os passos do ciclo ──────────────────────────────────────────────────────
// Cada um diz o que acontece no MUNDO (o carro, a cancela) e o que acontece nos
// SISTEMAS (servidor, tela, app, impressora). É a separação que o texto perde.
const PASSOS = [
  {
    id: 'chegada',
    fase: 'entrada',
    titulo: 'O carro chega',
    mundo: 'O veículo para diante da cancela de entrada. O laço indutivo no chão percebe o metal e abre a cancela.',
    sistema: 'Nada ainda. A cancela de entrada não espera pelo reconhecimento — ele acontece em paralelo.',
    carroX: 8, cancelaEntrada: 0, destaque: null,
  },
  {
    id: 'leitura-entrada',
    fase: 'entrada',
    titulo: 'A câmera lê a placa',
    mundo: 'A câmera de entrada acompanha o veículo por vários quadros enquanto ele avança.',
    sistema: 'O motor lê a placa em cada quadro e vota caractere a caractere entre as leituras da mesma passagem. Uma leitura ruim não decide sozinha.',
    carroX: 24, cancelaEntrada: 70, destaque: 'camera-entrada',
  },
  {
    id: 'ticket',
    fase: 'entrada',
    titulo: 'O ticket nasce',
    mundo: 'O carro passa pela cancela e entra no pátio.',
    sistema: 'O servidor abre o ticket com a placa consolidada, guarda a foto e o recorte como evidência, e avisa as telas.',
    carroX: 42, cancelaEntrada: 70, destaque: 'servidor',
  },
  {
    id: 'impressao',
    fase: 'entrada',
    titulo: 'A impressão e as telas',
    mundo: 'A impressora da guarita solta o ticket, com o número e o horário de entrada.',
    sistema: 'No mesmo instante o veículo aparece no Dashboard, na lista de Tickets e no app da guarita — em ordem de chegada, no topo.',
    carroX: 56, cancelaEntrada: 0, destaque: 'saidas',
  },
  {
    id: 'estacionado',
    fase: 'permanencia',
    titulo: 'O veículo permanece',
    mundo: 'O carro fica no pátio. O relógio da permanência corre.',
    sistema: 'O ticket está <b>ativo</b>. A tarifa que vale é a vigente no momento da entrada, mesmo que mude depois.',
    carroX: 72, cancelaEntrada: 0, destaque: null,
  },
  {
    id: 'pagamento',
    fase: 'pagamento',
    titulo: 'O cliente paga',
    mundo: 'O motorista vai ao caixa. O operador busca a placa, confere o valor e recebe.',
    sistema: 'O ticket passa para <b>pago</b> e ganha uma janela de saída: 15 minutos a partir do pagamento. O comprovante diz "Pode sair até".',
    carroX: 72, cancelaEntrada: 0, destaque: 'caixa',
  },
  {
    id: 'a-caminho',
    fase: 'saida',
    titulo: 'O carro vai até a cancela',
    mundo: 'O motorista caminha até o veículo e dirige até a saída. A cancela continua fechada.',
    sistema: '<b>O pagamento não abriu nada.</b> É a pergunta mais comum da operação: o ticket está pago, mas a cancela só decide quando o carro chegar.',
    carroX: 84, cancelaSaida: 0, destaque: 'aviso-cancela',
  },
  {
    id: 'leitura-saida',
    fase: 'saida',
    titulo: 'A câmera de saída lê',
    mundo: 'O veículo para diante da cancela de saída.',
    sistema: 'A câmera lê a placa e o servidor procura o ticket dela: existe? está pago? está dentro da janela? é mensalista ou credenciado ativo?',
    carroX: 90, cancelaSaida: 0, destaque: 'camera-saida',
  },
  {
    id: 'abre',
    fase: 'saida',
    titulo: 'A cancela abre',
    mundo: 'Conferido o pagamento, a cancela sobe e o veículo sai.',
    sistema: 'O servidor grava a saída com a foto, calcula a permanência real e finaliza o ticket. Só agora o veículo deixa de contar no pátio.',
    carroX: 96, cancelaSaida: 70, destaque: 'servidor',
  },
];

const FASES = {
  entrada: { label: 'Entrada', cor: 'var(--accent)' },
  permanencia: { label: 'Permanência', cor: 'var(--ink-500)' },
  pagamento: { label: 'Pagamento', cor: 'var(--ok)' },
  saida: { label: 'Saída', cor: 'var(--warn)' },
};

const MS_POR_PASSO = 3600;

/**
 * O mesmo ciclo em vídeo, para quem prefere assistir a navegar.
 *
 * Não é a mesma peça: a animação aqui é interativa e para em qualquer passo; o
 * vídeo é contínuo, tem capa e fecho, e serve para enviar a quem ainda não usa
 * a plataforma. Um não substitui o outro, e por isso ambos existem.
 *
 * Está como constante, e não como setting, porque é material institucional que
 * muda raramente. Se um dia precisar trocar sem deploy, vira `help_video_url`.
 */
const VIDEO = {
  url: 'https://youtu.be/xiNoXDVRIWc',
  duracao: '47s',
};

export default function FluxoAnimado() {
  const [passo, setPasso] = useState(0);
  const [tocando, setTocando] = useState(true);
  const timer = useRef(null);
  const p = PASSOS[passo];

  // Quem pediu menos movimento no sistema não recebe o avanço automático — mas
  // continua com os botões, porque desligar a animação não pode significar
  // perder o conteúdo.
  useEffect(() => {
    const menosMovimento = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (menosMovimento) setTocando(false);
  }, []);

  useEffect(() => {
    if (!tocando) return undefined;
    timer.current = setTimeout(
      () => setPasso(a => (a + 1) % PASSOS.length),
      MS_POR_PASSO
    );
    return () => clearTimeout(timer.current);
  }, [passo, tocando]);

  const ir = (i) => { setTocando(false); setPasso(i); };

  return (
    <section className="fluxo" aria-label="Como funciona o ciclo do estacionamento">
      <header className="fluxo-cabeca">
        <div>
          {/* Sem bordão: "do portão à cancela" andava em círculo — portão e
              cancela são quase a mesma coisa —, e o subtítulo abaixo já diz
              tudo o que a frase extra tentava dizer. */}
          <h3>Como funciona</h3>
          <p>O ciclo inteiro em nove passos. Toque num passo para parar nele.</p>
        </div>
        <div className="fluxo-acoes">
          <a
            className="fluxo-video"
            href={VIDEO.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            ▶ Ver em vídeo
            <small>{VIDEO.duracao} · abre no YouTube</small>
          </a>
          <button
            type="button"
            className="fluxo-botao"
            onClick={() => setTocando(t => !t)}
            aria-label={tocando ? 'Pausar a animação' : 'Retomar a animação'}
          >
            {tocando ? '❙❙ Pausar' : '▶ Tocar'}
          </button>
        </div>
      </header>

      <Cena p={p} />

      <div className="fluxo-legenda" role="status" aria-live="polite">
        <span className="fluxo-fase" style={{ '--cor-fase': FASES[p.fase].cor }}>
          {FASES[p.fase].label}
        </span>
        <h4>{p.titulo}</h4>
        <dl>
          <dt>No pátio</dt>
          <dd>{p.mundo}</dd>
          <dt>No sistema</dt>
          <dd dangerouslySetInnerHTML={{ __html: p.sistema }} />
        </dl>
      </div>

      <ol className="fluxo-passos">
        {PASSOS.map((x, i) => (
          <li key={x.id}>
            <button
              type="button"
              className={i === passo ? 'atual' : (i < passo ? 'feito' : '')}
              onClick={() => ir(i)}
              aria-current={i === passo ? 'step' : undefined}
              title={x.titulo}
            >
              <span className="num">{i + 1}</span>
              <span className="rotulo">{x.titulo}</span>
            </button>
          </li>
        ))}
      </ol>
    </section>
  );
}

/* ─── A cena ─────────────────────────────────────────────────────────────────
 * Desenhada em SVG com formas simples e cores dos tokens. O traço é
 * deliberadamente ingênuo — o objetivo é reconhecer as peças num relance, não
 * retratar o estacionamento.
 */
function Cena({ p }) {
  const d = p.destaque;
  return (
    <div className="fluxo-cena">
      <svg viewBox="0 0 1000 400" role="img"
           aria-label={`${p.titulo}. ${p.mundo}`}>
        <defs>
          <linearGradient id="ceu" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#dce9f0" />
            <stop offset="100%" stopColor="#eef3f6" />
          </linearGradient>
          <filter id="brilho" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        <rect width="1000" height="400" fill="url(#ceu)" />

        {/* asfalto */}
        <rect x="0" y="250" width="1000" height="110" fill="#3d4c55" />
        <g stroke="#e8eef1" strokeWidth="4" strokeDasharray="26 22" opacity=".55">
          <line x1="0" y1="305" x2="1000" y2="305" />
        </g>

        {/* vagas do pátio */}
        <g opacity=".9">
          <rect x="600" y="120" width="250" height="110" rx="6" fill="#e2eaee" stroke="#c6d2d9" />
          <text x="725" y="112" textAnchor="middle" className="cena-rot">PÁTIO</text>
          {[0, 1, 2, 3].map(i => (
            <rect key={i} x={614 + i * 59} y="132" width="48" height="86" rx="4"
                  fill="#f4f7f9" stroke="#c6d2d9" strokeDasharray="5 4" />
          ))}
        </g>

        {/* ── entrada ── */}
        <Poste x={150} />
        <Camera x={150} y={92} ativo={d === 'camera-entrada'} rotulo="CÂMERA ENTRADA" />
        <Cancela x={185} aberturaGraus={p.cancelaEntrada || 0} />
        <text x="150" y="378" textAnchor="middle" className="cena-rot">ENTRADA</text>

        {/* ── saída ── */}
        <Poste x={880} />
        <Camera x={880} y={92} ativo={d === 'camera-saida'} rotulo="CÂMERA SAÍDA" />
        <Cancela x={915} aberturaGraus={p.cancelaSaida || 0} />
        <text x="880" y="378" textAnchor="middle" className="cena-rot">SAÍDA</text>

        {/* ── guarita, com impressora e app ── */}
        <Guarita ativo={d === 'saidas'} />

        {/* ── servidor ── */}
        <Servidor ativo={d === 'servidor'} />

        {/* ── caixa ── */}
        <Caixa ativo={d === 'caixa'} />

        {/* ── o carro ── */}
        <Carro x={p.carroX} />

        {/* aviso do passo que explica a cancela */}
        {d === 'aviso-cancela' && (
          <g className="cena-aviso">
            <rect x="600" y="20" width="360" height="58" rx="8"
                  fill="var(--warn-soft)" stroke="var(--warn)" />
            <text x="780" y="44" textAnchor="middle" className="cena-aviso-t">
              Pago não é o mesmo que liberado
            </text>
            <text x="780" y="64" textAnchor="middle" className="cena-aviso-s">
              a cancela decide na leitura da placa
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}

function Poste({ x }) {
  return <rect x={x - 4} y="100" width="8" height="150" fill="#8fa3ad" rx="3" />;
}

function Camera({ x, y, ativo, rotulo }) {
  return (
    <g className={ativo ? 'cena-cam ativo' : 'cena-cam'}>
      {ativo && (
        <path d={`M ${x} ${y + 22} L ${x - 58} 258 L ${x + 58} 258 Z`}
              fill="var(--accent)" opacity=".18" />
      )}
      <rect x={x - 22} y={y} width="44" height="26" rx="5"
            fill={ativo ? 'var(--accent)' : '#5b7280'} filter={ativo ? 'url(#brilho)' : undefined} />
      <circle cx={x + 14} cy={y + 13} r="6" fill="#10202a" />
      <circle cx={x + 14} cy={y + 13} r="2.5" fill={ativo ? '#7fe3ff' : '#4a6270'} />
      <text x={x} y={y - 10} textAnchor="middle" className="cena-rot-sm">{rotulo}</text>
    </g>
  );
}

function Cancela({ x, aberturaGraus }) {
  return (
    <g>
      <rect x={x - 7} y="215" width="14" height="42" rx="3" fill="#7d939f" />
      <g style={{ transform: `rotate(${-aberturaGraus}deg)`, transformOrigin: `${x}px 218px`, transition: 'transform 900ms cubic-bezier(.2,.8,.2,1)' }}>
        <rect x={x} y="212" width="86" height="11" rx="4" fill="#b4302b" />
        {[0, 1, 2].map(i => (
          <rect key={i} x={x + 12 + i * 26} y="212" width="13" height="11" fill="#f4f7f9" />
        ))}
      </g>
    </g>
  );
}

function Carro({ x }) {
  const px = (x / 100) * 1000;
  return (
    <g style={{ transform: `translateX(${px}px)`, transition: 'transform 1500ms cubic-bezier(.4,.0,.2,1)' }}>
      <g transform="translate(-46,268)">
        <rect x="0" y="14" width="92" height="26" rx="9" fill="var(--accent)" />
        <path d="M 16 14 L 26 0 L 66 0 L 78 14 Z" fill="#1d7d94" />
        <rect x="28" y="3" width="34" height="10" rx="2" fill="#cfe6ee" opacity=".9" />
        <circle cx="22" cy="42" r="9" fill="#22323b" />
        <circle cx="70" cy="42" r="9" fill="#22323b" />
        <circle cx="22" cy="42" r="3.5" fill="#8fa3ad" />
        <circle cx="70" cy="42" r="3.5" fill="#8fa3ad" />
        <rect x="60" y="24" width="26" height="11" rx="2" fill="#f4f7f9" stroke="#10202a" strokeWidth="1" />
        <text x="73" y="32.5" textAnchor="middle" className="cena-placa">ABC1D23</text>
      </g>
    </g>
  );
}

function Guarita({ ativo }) {
  return (
    <g className={ativo ? 'cena-bloco ativo' : 'cena-bloco'}>
      <rect x="250" y="128" width="150" height="102" rx="8"
            fill="var(--surface)" stroke={ativo ? 'var(--accent)' : 'var(--line)'} strokeWidth={ativo ? 2.5 : 1.5} />
      <text x="325" y="150" textAnchor="middle" className="cena-rot">GUARITA</text>
      {/* impressora */}
      <rect x="266" y="160" width="54" height="30" rx="4" fill="#5b7280" />
      <rect x="272" y="186" width="42" height="34" rx="2" fill="#ffffff" stroke="#c6d2d9"
            className={ativo ? 'cena-papel saindo' : 'cena-papel'} />
      <text x="293" y="200" textAnchor="middle" className="cena-mini">TICKET</text>
      <text x="293" y="211" textAnchor="middle" className="cena-mini">ABC1D23</text>
      {/* celular */}
      <rect x="336" y="160" width="42" height="62" rx="6" fill="#10202a" />
      <rect x="340" y="166" width="34" height="50" rx="3" fill={ativo ? 'var(--accent-soft)' : '#dde5ea'} />
      <rect x="344" y="172" width="26" height="6" rx="2" fill={ativo ? 'var(--accent)' : '#a8b8c1'} />
      <rect x="344" y="182" width="26" height="5" rx="2" fill="#c6d2d9" />
      <rect x="344" y="190" width="26" height="5" rx="2" fill="#c6d2d9" />
      <text x="357" y="234" textAnchor="middle" className="cena-rot-sm">APP</text>
    </g>
  );
}

function Servidor({ ativo }) {
  return (
    <g className={ativo ? 'cena-bloco ativo' : 'cena-bloco'}>
      <rect x="440" y="30" width="120" height="86" rx="8"
            fill="var(--surface)" stroke={ativo ? 'var(--accent)' : 'var(--line)'} strokeWidth={ativo ? 2.5 : 1.5}
            filter={ativo ? 'url(#brilho)' : undefined} />
      <text x="500" y="50" textAnchor="middle" className="cena-rot">SERVIDOR</text>
      {[0, 1, 2].map(i => (
        <g key={i}>
          <rect x="456" y={58 + i * 17} width="88" height="12" rx="3" fill="var(--surface-2)" stroke="var(--line-soft)" />
          <circle cx={464} cy={64 + i * 17} r="3" fill={ativo ? 'var(--ok)' : '#c6d2d9'} />
        </g>
      ))}
    </g>
  );
}

function Caixa({ ativo }) {
  return (
    <g className={ativo ? 'cena-bloco ativo' : 'cena-bloco'}>
      <rect x="620" y="30" width="128" height="70" rx="8"
            fill="var(--surface)" stroke={ativo ? 'var(--ok)' : 'var(--line)'} strokeWidth={ativo ? 2.5 : 1.5} />
      <text x="684" y="50" textAnchor="middle" className="cena-rot">CAIXA</text>
      <rect x="636" y="58" width="96" height="28" rx="4" fill={ativo ? 'var(--ok-soft)' : 'var(--surface-2)'} stroke="var(--line-soft)" />
      <text x="684" y="77" textAnchor="middle" className="cena-valor"
            fill={ativo ? 'var(--ok-ink)' : 'var(--ink-400)'}>R$ 10,00</text>
    </g>
  );
}
